import React, { useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './components/hooks/AuthProvider';
import styled, { createGlobalStyle } from 'styled-components';
import SignUp from './components/auth/SignUp';
import Login from './components/auth/Login';
import Header from './components/dashboards/Header';
import AdminDashboard from './components/dashboards/AdminDashboard';
import SupervisorDashboard from './components/dashboards/SupervisorDash';
import SprayTechDashboard from './components/dashboards/SprayTechDash';
import ChemAppForm from './components/Services Logic/ChemAppForm';
import JobForm from './components/Services Logic/JobForm';
import AdminJobList from './components/Services Logic/AdminJobList';
import JobList from './components/Services Logic/Job List/JobList';
import ManagerDashboard from './components/dashboards/ManagerDash9';
import JobDetails from './components/Services Logic/JobDetails';
import ErrorBoundary from './components/Helper Functions/ErrorBoundary';
import SupervisorServiceList from './components/Services Logic/SupervisorServicesList';
import ManagerJobForm from './components/Services Logic/ManagerJob.Form';
import Notes from './components/Notes/Notes';
import TaskList from './components/TaskList';
import AdminTaskList from './components/AdminTaskList';
import ImageUpload from './components/Services Logic/ImageUpload';
import ImageGallery from './components/Services Logic/ImageGallery';
import './i18n/i18n';
import ChecklistForm from './components/TruckServices/ChecklistForm';
import VehicleMaintenance from './components/TruckServices/VehicleMaintenance';
import TripHistory from './components/TruckServices/TripHistory';
import BidProposalDemo from './components/EbidLite/BidProposalDemo';
import SupervisorTripInspection from './components/TruckServices/SupervisorTripInspection';
import UserManagement from './components/AdminFunctions/UserManagement';
import InspectionHistory from './components/TruckServices/InspectionHistory';
import VehicleInspectionList from './components/AdminFunctions/VehicleInpsectionList';
import PunchListViewer from './components/PunchListViewer';
import ManagerInspectionHistory from './components/TruckServices/ManagerInspectionHistory';
import WeeklyReport from './components/AdminFunctions/WeeklyReport';
import SavedReports from './components/Services Logic/SavedReports';
import SprayTechManagerDashboard from './components/dashboards/SprayTechManagerDash';
import PendingApplications from './components/Services Logic/TechChemAppList';
import ManagerChemAppList from './components/Services Logic/ManagerChemAppList';
import EditablePendingApplications from './components/Services Logic/EditChemForm';
import ChemicalReviewPage from './components/Services Logic/ChemicalReviewPage';
import IncidentReportForm from './components/ReportForms/IncidentReportForm';
import ReportDrafts from './components/ReportForms/ReportDrafts';
import IncidentSummaryCard from './components/ReportForms/IncidentEmailSummaryCard';
import CheckRequestForm from './components/ReportForms/CheckRequestForm';
import IncidentReportWorkflowDashboard from './components/dashboards/IncidentReportDash';
import PayoutRequestForm from './components/ReportForms/PayoutRequest';
import VendorPaymentRequestForm from './components/ReportForms/VendorPaymentRequestForm';
import IncidentReportViewer from './components/ReportForms/IncidentReportFormViewer';
import IncidentReportList from './components/ReportForms/IncidentReportList';
import PayrollDeductionForm from './components/ReportForms/PayrollDeductionForm';
import ChangeOfStatusForm from './components/ReportForms/ChangeofStatusForm';
import GlobalErrorScreen from './components/GlobalErrorScreen';
import { db } from './components/Firebase Functions/firebase';
import { doc, getDoc } from 'firebase/firestore';





const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #f5f5f5;
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
  }
`;

const Footer = styled.footer`
  background-color: #296b1a;
  color: white;
  text-align: center;
  padding: 1rem;
  position: fixed;
  bottom: 0;
  width: 100%;
`;

// ProtectedRoute component definition
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, loading } = useContext(AuthContext);
  const location = useLocation();

  console.log("🔥 ProtectedRoute - Checking Access for Route:", location.pathname);
  console.log("🔍 ProtectedRoute - Current User:", currentUser);
  console.log("🔍 Loading Authentication State:", loading);
  console.log("🔍 Allowed Roles for this Route:", allowedRoles);

  // ✅ Prevent redirect while Firebase is still loading authentication state
  if (loading) {
    console.log("🔄 Authentication is still loading, waiting...");
    return <p>Loading authentication...</p>; // Optional: Replace with a spinner
  }

  // ✅ If user is not logged in, redirect to login page
  if (!currentUser) {
    console.warn("🚨 No user found after authentication check, redirecting to login...");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ✅ Normalize user role and allowed roles
  const userRole = currentUser?.type?.trim().toLowerCase() || "undefined";
  const normalizedAllowedRoles = allowedRoles.map(role => role.trim().toLowerCase());

  //console.log("🔥 Normalized User Role:", userRole);
  //console.log("🔥 Normalized Allowed Roles:", normalizedAllowedRoles);

  // ✅ Role authorization check
  const isAuthorized = normalizedAllowedRoles.includes(userRole);
  //console.log("🔥 Final Role Check Result:", isAuthorized ? "✅ PASSED" : "❌ FAILED");

  if (!isAuthorized) {
    console.warn("⛔ User does not have the required role, redirecting to unauthorized...");
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

console.log("🧩 ReportDrafts import type:", typeof ReportDrafts);

const App = () => {
  const [isAppDown, setIsAppDown] = useState(false);

  const checkNetwork = useCallback(async () => {
    try {
      // Try to read the "healthCheck/status" doc
      const docRef = doc(db, "healthCheck", "status");
      await getDoc(docRef);
      setIsAppDown(false); // App is up
    } catch (error) {
      console.error("Network error detected:", error);
      setIsAppDown(true); // App is down
    }
  }, []);

  useEffect(() => {
    checkNetwork();

    // Optional: check every 60 seconds
    const interval = setInterval(() => {
      checkNetwork();
    }, 60000); // 1 min

    return () => clearInterval(interval);
  }, [checkNetwork]);
    
  if (isAppDown) {
    return <GlobalErrorScreen />;
  }

  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <AppContainer>
            <GlobalStyle />
            <Header />
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route path="/vehicle-maintenance" element={<VehicleMaintenance />} />
              <Route path="/incident-report/resume/:draftId" element={<IncidentReportForm />} />
              <Route path="/incident-email-summary" element={<IncidentSummaryCard />} />
              <Route path="/check-request-form" element={<CheckRequestForm />} />
              <Route path="/incident-dash" element={<IncidentReportWorkflowDashboard />} />
              <Route path="/incident-report/view/:id" element={<IncidentReportViewer />} />




              <Route
                path="/spray-tech-dashboard"
                element={
                  <ProtectedRoute allowedRoles={['spray_tech', 'admin']}>
                    <SprayTechDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/chem-app-form"
                element={
                  <ProtectedRoute allowedRoles={['spray_tech', 'admin', 'spray_manager']}>
                    <ChemAppForm />
                  </ProtectedRoute>
                }
              />

<Route
                path="/pending-forms"
                element={
                  <ProtectedRoute allowedRoles={['spray_tech', 'admin', 'spray_manager']}>
                    <PendingApplications />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/job-form"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}>
                    <JobForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/job-list"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'spray_tech']}>
                    <JobList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-job-list"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminJobList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/all-inpsections"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <VehicleInspectionList />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/jobs/:id"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'supervisor', 'admin']}>
                    <JobDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/saved-reports"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'supervisor', 'admin']}>
                    <SavedReports />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin-dash"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

               <Route
                path="/incident-list"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <IncidentReportList />
                  </ProtectedRoute>
                }
              /> 

              <Route
                path="/user-management"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <UserManagement />
                  </ProtectedRoute>
                }
              />

<Route
    path="/image-gallery"
    element={
      <ProtectedRoute allowedRoles={['admin']}>
        <ImageGallery />
      </ProtectedRoute>
    }
  />

              <Route
                path="/admin-punch-list"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminTaskList />
                  </ProtectedRoute>
                }
              /> 

              <Route
                path="/weekly-report"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <WeeklyReport />
                  </ProtectedRoute>
                }
              />              

              <Route
                path="/manager-dashboard"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'admin']}>
                    <ManagerDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/change-of-status"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'admin', 'spray_manager']}>
                    <ChangeOfStatusForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/edit-chemical-form/:id"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'admin', 'spray_manager']}>
                    <EditablePendingApplications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chemical-review/:id"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'admin', 'spray_manager']}>
                    <ChemicalReviewPage />
                  </ProtectedRoute>
                }
              />

<Route
  path="/bid-proposal"
  element={
    <ProtectedRoute allowedRoles={['manager', 'admin']}>
      <BidProposalDemo />
    </ProtectedRoute>
  }
/>


              <Route
                path="/manager-job-form"
                element={
                  <ProtectedRoute allowedRoles={['manager', 'admin']}>
                    <ManagerJobForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/supervisor-dashboard"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'admin']}>
                    <SupervisorDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/supervisor-job-list"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'admin']}>
                    <SupervisorServiceList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/truck-checklist"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                    <ChecklistForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/supervisor-trip-inspection"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                    <SupervisorTripInspection />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inspection-history"
                element={
                  <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_manager', 'spray_tech']}>
                    <InspectionHistory />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inspection-history"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'spray_manager']}>
                    <TripHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/incident-report"
                element={
                   <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                     <IncidentReportForm />
                   </ProtectedRoute>
                }
              />
              <Route
                path="/saved-incident-reports"
                element={
                   <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                     <ReportDrafts />
                   </ProtectedRoute>
                }
              />
              <Route
                path="/payout-request"
                element={
                   <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                     <PayoutRequestForm />
                   </ProtectedRoute>
                }
              />
              <Route
                path="/vendor-request"
                element={
                   <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                     <IncidentSummaryCard />
                   </ProtectedRoute>
                }
              />
              <Route
                path="/payroll-deduction"
                element={
                   <ProtectedRoute allowedRoles={['supervisor', 'admin', 'manager', 'spray_tech', 'spray_manager']}>
                     <PayrollDeductionForm />
                   </ProtectedRoute>
                }
              />

          


              <Route path="/notes" element={<Notes />} />
              <Route path="/upload-images" element={<ImageUpload />} />

              <Route
                path="/tasks"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'spray_manager']}>
                    <TaskList />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/manager-spray-list"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'spray_manager']}>
                    <ManagerChemAppList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/view-punch-lists"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'spray_manager']}>
                    <PunchListViewer />
                  </ProtectedRoute>
                }
              />
               <Route
                path="/spray-manager-dash"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'spray_manager']}>
                    <SprayTechManagerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager-inspection-history"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <ManagerInspectionHistory />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AppContainer>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
